// NOTICE: This file was copied from the oppizi-shared repository, do
// not modify it here.

import { InvalidRequest } from '../error'

export const EARTH_RADIUS = 6371 // in km
export const EARTH_RADIUS_METERS = 6378137 // in meters

/**
 * Checks that location is provided and has latitude and longitude fields
 * @param {Object} location
 * @param {number} location.latitude
 * @param {number} location.longitude
 * @returns {boolean}
 */
export const isValidLocationPoint = location =>
  location &&
  typeof location.latitude === 'number' &&
  typeof location.longitude === 'number'

// Using the ‘Haversine’ formula
export const getDistance = (latA, lngA, latB, lngB) => {
  const deg2rad = deg => deg * (Math.PI / 180)
  const dLat = deg2rad(latB - latA) // deg2rad below
  const dLon = deg2rad(lngB - lngA)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(latA)) *
      Math.cos(deg2rad(latB)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)

  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return EARTH_RADIUS * c * 1000 // Distance in meters
}

/**
 *
 * @typedef {Array<number>} Point
 * @property {number} 0 First element of the array, x coordinate
 * @property {number} 1 Second element of the array, y coordinate
 */

/**
 * Checks that provided point is inside the polygon
 * @param {Point} point
 * @param {Array<Point>} polygon
 * @returns {Boolean}
 */
export const isPointInPolygon = (point, polygon) => {
  if (!Array.isArray(point)) throw new InvalidRequest('Point is not valid')
  if (!Array.isArray(polygon) || polygon.some(p => !Array.isArray(p)))
    throw new InvalidRequest('polygon is not valid')

  // ray-casting algorithm based on
  // https://rosettacode.org/wiki/Ray-casting_algorithm PNPoly

  const [x, y] = point

  let inside = false
  for (var i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i]
    const [xj, yj] = polygon[j]

    /*eslint-disable*/
    const intersect =
      yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    /*eslint-enable*/
    if (intersect) inside = !inside
  }

  return inside
}

/**
 *
 * @typedef {Object} LocationPoint
 * @property {number} longitude
 * @property {number} latitude
 */
/**
 * Checks that provided location point is inside the polygon
 * @param {LocationPoint} point
 * @param {Array<LocationPoint>} polygon
 * @returns {Boolean}
 */
export const isPointInPolygonLocation = (point, polygon) => {
  const convertLocationObjectToArray = ({ longitude, latitude }) => [
    longitude,
    latitude,
  ]

  const geometricalPoint = convertLocationObjectToArray(point)
  const geometricalPolygon = polygon.map(convertLocationObjectToArray)

  return isPointInPolygon(geometricalPoint, geometricalPolygon)
}

/**
 * Get distance to closest point. Return 0 if the point is in the polygon.
 * @param {LocationPoint} location
 * @param {Array<LocationPoint>} polygon
 * @returns {number}
 */
export const getDistanceToPolygonPoint = (location, polygon) => {
  if (!isValidLocationPoint(location))
    throw new InvalidRequest('Location is not provided')
  if (
    !Array.isArray(polygon) ||
    polygon.length === 0 ||
    !polygon.every(isValidLocationPoint)
  )
    throw new InvalidRequest('Boundaries are not provided')

  if (isPointInPolygonLocation(location, polygon)) return 0

  const distances = polygon.map(point =>
    getDistance(
      location.latitude,
      location.longitude,
      point.latitude,
      point.longitude,
    ),
  )

  return Math.min(...distances)
}

/**
 *
 * @typedef {Object} LocationPointWithDelta
 * @property {number} longitude
 * @property {number} latitude
 * @property {number} longitudeDelta
 * @property {number} latitudeDelta
 */
/**
 * Calculate center point and lat- ,lon- deltas of polygon (width and height)
 * Note: return format is compatible with react-native-maps `region` prop
 * @param {Array<LocationPoint>} polygon array of location points
 * @param {number} marginDelta margin for deltas. Can be used to display polygon on map with margins
 * @returns {LocationPointWithDelta}
 */
export const getPolygonCentreAndDelta = (polygon, marginDelta = 0) => {
  const latitudesList = polygon.map(({ latitude }) => latitude)
  const longitudesList = polygon.map(({ longitude }) => longitude)

  const minLat = Math.min(...latitudesList)
  const maxLat = Math.max(...latitudesList)

  const minLon = Math.min(...longitudesList)
  const maxLon = Math.max(...longitudesList)

  const latitudeDelta = maxLat - minLat
  const longitudeDelta = maxLon - minLon
  const centralLatitude = minLat + latitudeDelta / 2
  const centralLongitude = minLon + longitudeDelta / 2

  return {
    latitudeDelta: latitudeDelta + marginDelta,
    longitudeDelta: longitudeDelta + marginDelta,
    latitude: centralLatitude,
    longitude: centralLongitude,
  }
}
/**
 * Calculates polygon area by geo coordinates using earth radius (m^2)
 * @param {Array<Object>} coords Array of coords objects
 * @param {Number} coords[0].latitude Latitude
 * @param {Number} coords[0].longitude Longitude
 *
 * @returns {Number} Polygon area in m^2
 *
 * @example getPolygonArea([
 *   { latitude:0, longitude:0 }
 *   { latitude:0, longitude:4 }
 *   { latitude:4, longitude:4 }
 *   { latitude:4, longitude:0 }
 * ])
 */
export const getPolygonArea = coords => {
  let area = 0
  if (coords && coords.length > 0) {
    area += Math.abs(ringArea(coords))
  }
  return area
}

/**
 * Calculates polygon area by geo coordinates using earth radius (km^2)
 * @param {Array<Object>} coords Array of coords objects
 * @param {Number} coords[0].latitude Latitude
 * @param {Number} coords[0].longitude Longitude
 *
 * @returns {Number} Polygon area in km^2
 *
 * @example getPolygonAreaInKm2([
 *   { latitude:0, longitude:0 }
 *   { latitude:0, longitude:4 }
 *   { latitude:4, longitude:4 }
 *   { latitude:4, longitude:0 }
 * ])
 */

export const getPolygonAreaInKm2 = coords => getPolygonArea(coords) / 1000000

/**
 * Calculates area density by geo coordinates and households amount
 * Result is people per square kilometer
 * @param {Array<Object>} arg.polygon Array of coords objects of the polygon
 * @param {Number} arg.householdsAmount Amount of the households in the polygon
 *
 * @returns {Number} Density of the households inside the polygon
 *
 * @example densityInPolygon({
 *  householdsAmount: 100,
 *  polygon:[
 *   { latitude: 0, longitude: 0 }
 *   { latitude: 0, longitude: 4 }
 *   { latitude: 4, longitude: 4 }
 *   { latitude: 4, longitude: 0 }
 *  ],
 * })
 */
export const densityInPolygon = ({ polygon, householdsAmount }) => {
  if (!polygon || !householdsAmount) return null
  const convertSqMetersToSqKilometers = area => area / 1000000

  const area = getPolygonArea(polygon)
  const density = householdsAmount / convertSqMetersToSqKilometers(area)

  return Math.round(density)
}

/**
 * Util function for getPolygonArea, calculates the area of the received shape
 */
const ringArea = points => {
  const rad = coord => (coord * Math.PI) / 180
  let p1
  let p2
  let p3
  let lowerIndex
  let middleIndex
  let upperIndex
  let area = 0

  if (points.length > 2) {
    for (let i = 0; i < points.length; i++) {
      if (i === points.length - 2) {
        lowerIndex = points.length - 2
        middleIndex = points.length - 1
        upperIndex = 0
      } else if (i === points.length - 1) {
        lowerIndex = points.length - 1
        middleIndex = 0
        upperIndex = 1
      } else {
        lowerIndex = i
        middleIndex = i + 1
        upperIndex = i + 2
      }
      p1 = points[lowerIndex]
      p2 = points[middleIndex]
      p3 = points[upperIndex]
      area +=
        (rad(p3.longitude) - rad(p1.longitude)) * Math.sin(rad(p2.latitude))
    }
    area = (area * EARTH_RADIUS_METERS * EARTH_RADIUS_METERS) / 2
  }

  return area
}
/**
 * Get distance to closest polygon border.
 *
 * @param {array} locationPoint Point for which distance should be calculated
 * @param {array} line Line to which distance should be calculated
 * @returns {number} Distance between point and line
 */
export const distanceToLine = ([px, py], [[l1x, l1y], [l2x, l2y]]) => {
  const xD = l2x - l1x
  const yD = l2y - l1y

  const u = ((px - l1x) * xD + (py - l1y) * yD) / (xD * xD + yD * yD)

  let closestLine
  if (u < 0) {
    closestLine = [l1x, l1y]
  } else if (u > 1) {
    closestLine = [l2x, l2y]
  } else {
    closestLine = [l1x + u * xD, l1y + u * yD]
  }
  return getDistance(px, py, closestLine[0], closestLine[1])
}

/**
 * Get distance to closest polygon border.
 *
 * @typedef LocationPoint
 * @property {number} longitude
 * @property {number} latitude
 *
 * @param {LocationPoint} locationPoint Point for which distance should be calculated
 * @param {array<LocationPoint>} polygon Polygon to which distance should be calculated
 * @returns {number} Distance between point and polygon
 */
export const getDistanceToPolygonBorder = ([px, py], vertices) => {
  const comp = vertices.reduce(
    ({ prevPoint, dist }, currPoint) => {
      const currDist = distanceToLine([px, py], [prevPoint, currPoint])
      const ret = {
        prevPoint: currPoint,
        dist,
      }
      if (currDist < dist) {
        ret.dist = currDist
      }
      return ret
    },
    { prevPoint: vertices[vertices.length - 1], dist: Infinity },
  )
  return comp.dist
}

/**
 * Calculates approximate distribution time
 * Result is time in hour
 * @param {Number} areaSize square of mission area - km^2
 * @param {Number} householdsAmount amount of households in area - in km^2
 * @param {Number} flyersAmount amount of flyers in mission - number
 * @param {Number} speed average human speed - in km/t
 * @returns {Number} average mission time - in hour
 *
 *
 * @example getTimeNeededForFlyersDistribution
 * getTimeNeededForFlyersDistribution({
 *   areaSize: 0.254,
 *   flyersAmount: 1000,
 *   speed: 3.5,
 *   householdsAmount: 915,
 * })
 *
 */
export const getTimeNeededForFlyersDistribution = ({
  areaSize,
  householdsAmount,
  flyersAmount = 1000,
  speed = 3.5,
}) => {
  // Approximately 30%
  const modularPathConversionFactor = 1.3
  const approximateDistanceBetweenHouseholds =
    areaSize / Math.sqrt(householdsAmount)
  const distanceToGo =
    flyersAmount *
    approximateDistanceBetweenHouseholds *
    modularPathConversionFactor
  const time = distanceToGo / speed
  return time
}

/**
 * Checks if country is supported by PathFinder
 * @param {String} countryCode Country code
 * @returns {Boolean} Indicates whether if country is supported by PathFinder
 * @example isPathFinderSupportsCountry('UK') - will return 'true'
 */

export const isPathFinderSupportsCountry = countryCode => {
  const countriesSupportedByPathFinder = ['AU', 'UK', 'NZ', 'FR', 'US', 'BE']
  return countriesSupportedByPathFinder.includes(countryCode)
}



// WEBPACK FOOTER //
// ./src/shared/libraries/geo.js